/**
 * Human-handoff (operator queue) API service (Phase 4.4).
 *
 * Tenant comes from the global `x-tenant-id` header (axios interceptor). The
 * status/agent filters are passed as query params. Pure async functions — no
 * React state.
 */
import { get, patch } from '@core/api/client';
import { endpoints } from '@core/api/endpoints';

// ── Domain types (mirror the backend `Escalation` payload) ────────────────────

export type EscalationReason =
  | 'user_requested'
  | 'agent_unable'
  | 'frustrated_user'
  | 'sensitive_topic'
  | 'other';

export type EscalationUrgency = 'low' | 'normal' | 'high';

export type EscalationStatus = 'pending' | 'acknowledged' | 'resolved';

/** Origin channel a handoff was raised from (V — visibility). */
export type EscalationChannel = 'web' | 'voice' | 'phone' | 'web_voice';

export interface Escalation {
  id: string;
  tenantId: string;
  agentId: string;
  sessionId: string;
  reason: EscalationReason;
  /** The agent's summary of what the visitor needs (operator context). */
  summary: string;
  urgency: EscalationUrgency;
  contact: Record<string, string> | null;
  status: EscalationStatus;
  note: string | null;
  /** The originating session's channel (V — visibility). */
  channel: EscalationChannel;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
}

export interface ListHandoffsQuery {
  status?: EscalationStatus | null;
  agentId?: string | null;
}

export interface UpdateHandoffPayload {
  status: EscalationStatus;
  note?: string | null;
}

// ── Service ───────────────────────────────────────────────────────────────────

/** How a phone handoff's transfer ended (Prompt 3.5). */
export type PhoneHandoffOutcome =
  | 'answered'
  | 'back_to_ai'
  | 'voicemail'
  | 'pending'
  | 'not_answered'
  | 'not_transferred';

export interface PhoneHandoffOutcomeRow {
  escalationId: string;
  callId: string;
  label: string | null;
  outcome: PhoneHandoffOutcome;
  /** Why the transfer did not connect (no-answer, busy, failed, caller_hung_up…). */
  reason?: string | null;
}

export const handoffService = {
  /** 3.5: the phone call behind each handoff and its transfer outcome. */
  phoneOutcomes(ids: string[]): Promise<PhoneHandoffOutcomeRow[]> {
    if (ids.length === 0) return Promise.resolve([]);
    return get<PhoneHandoffOutcomeRow[]>(endpoints.telephony.handoffOutcomes, {
      params: { ids: ids.join(',') },
    });
  },
  list(query: ListHandoffsQuery = {}): Promise<Escalation[]> {
    const params: Record<string, string> = {};
    if (query.status) params.status = query.status;
    if (query.agentId) params.agentId = query.agentId;
    return get<Escalation[]>(endpoints.handoffs.list, { params });
  },

  update(id: string, payload: UpdateHandoffPayload): Promise<Escalation> {
    return patch<Escalation>(endpoints.handoffs.update(id), payload);
  },
} as const;
